import { Stack, Typography } from '@mui/material';
import { MultiSectionDigitalClock, TimeField } from '@mui/x-date-pickers';
import { type KeyboardEvent } from 'react';

import { type Dayjs } from '@/utils/dayjs';

const handleTimeFieldKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
  if (e.key === 'Tab') {
    e.preventDefault();
    const arrowKey = e.shiftKey ? 'ArrowLeft' : 'ArrowRight';
    e.currentTarget.dispatchEvent(
      new KeyboardEvent('keydown', { key: arrowKey, bubbles: true })
    );
  }
};

export const TimePicker = ({
  value,
  setValue,
  title = 'Time:',
  showSeconds = false,
  minTime,
  maxTime,
}: {
  value: Dayjs;
  setValue: (value: Dayjs) => void;
  title?: string;
  showSeconds?: boolean;
  minTime?: Dayjs;
  maxTime?: Dayjs;
}) => {
  return (
    <Stack className='p-[24px] w-[308px] h-[348px]' gap='24px'>
      <Stack direction='row' justifyContent='space-between'>
        <Typography variant='subtitle1' className='mt-[4px]'>
          {title}
        </Typography>
        <TimeField
          ampm={false}
          minTime={minTime}
          maxTime={maxTime}
          format={showSeconds ? 'HH:mm:ss' : 'HH:mm'}
          value={value}
          onKeyDown={handleTimeFieldKeyDown}
          onChange={(value) => {
            if (value) {
              setValue(value);
            }
          }}
          sx={{
            width: showSeconds ? '100px' : '80px',

            '& .MuiInputBase-root': {
              padding: 0,
              fontSize: '13px',
            },

            '& .MuiOutlinedInput-input': {
              padding: '4px 12px',
            },

            '& .MuiOutlinedInput-notchedOutline': {
              border: '1px solid var(--mui-palette-primary-main)',
            },
          }}
        />
      </Stack>
      <MultiSectionDigitalClock
        autoFocus={false}
        minTime={minTime}
        maxTime={maxTime}
        ampm={false}
        views={
          showSeconds ? ['hours', 'minutes', 'seconds'] : ['hours', 'minutes']
        }
        timeSteps={{ minutes: 1, seconds: 1 }}
        value={value}
        onChange={(value) => {
          if (value) {
            setValue(value);
          }
        }}
        sx={{
          flexGrow: 1,
          border: '1px solid var(--mui-palette-divider)',

          '& .MuiList-root': {
            flexGrow: 1,
            width: showSeconds ? '33.33%' : '50%',
            padding: '4px',
            maxHeight: '100%',
          },

          '& .MuiMenuItem-root': {
            padding: '12px 8px',
            width: '100%',
            margin: 0,
            justifyContent: 'start',

            '&:hover': {
              backgroundColor: 'var(--mui-palette-primary-dark)',
            },
          },

          '& .Mui-focusVisible': {
            backgroundColor: 'var(--mui-palette-primary-dark) !important',
          },

          '& .Mui-focusVisible.Mui-selected': {
            backgroundColor: 'var(--mui-palette-primary-main) !important',
          },
        }}
      />
    </Stack>
  );
};
